import { useCallback, useRef, useSyncExternalStore } from 'react'
import * as Y from 'yjs'
import {
  DEFAULT_IFRAME_VIEWPORT,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_PAGE_FACTOR,
  DEFAULT_TEXT_COLOR,
  Y_ELEMENTS,
  type BoardElement,
  type ElementType,
  type RichDoc,
} from '@praesto/shared'
import { isRichDoc, richFromPlain } from '../canvas/richText.js'

type YElement = Y.Map<unknown>

const elementsMap = (doc: Y.Doc): Y.Map<YElement> => doc.getMap<YElement>(Y_ELEMENTS)

function readElement(id: string, map: YElement): BoardElement | null {
  const type = map.get('type') as ElementType | undefined
  if (!type) return null

  const base = {
    id,
    x: Number(map.get('x') ?? 0),
    y: Number(map.get('y') ?? 0),
    w: Number(map.get('w') ?? 100),
    h: Number(map.get('h') ?? 100),
    rotation: Number(map.get('rotation') ?? 0),
    z: Number(map.get('z') ?? 0),
  }

  if (type === 'text') {
    const rich = map.get('rich')
    return {
      ...base,
      type,
      text: String(map.get('text') ?? ''),
      // Nur die gewöhnliche Fassung wird gelesen, nie das Yjs-Fragment: Dieser
      // Schnappschuss entsteht bei jeder Änderung am Board neu und wandert in
      // Vorlagen und Verlauf — er muss schlicht und billig bleiben.
      ...(isRichDoc(rich) ? { rich } : {}),
      fontSize: Number(map.get('fontSize') ?? 32),
      color: String(map.get('color') ?? DEFAULT_TEXT_COLOR),
      align: (map.get('align') as 'left' | 'center' | 'right') ?? 'left',
      bold: Boolean(map.get('bold') ?? false),
      serif: Boolean(map.get('serif') ?? false),
      // Leer heißt „Schrift des Boards"; das ist auch der Stand jedes Textes,
      // der vor der freien Schriftwahl entstanden ist.
      font: String(map.get('font') ?? ''),
      lineHeight: Number(map.get('lineHeight') ?? DEFAULT_LINE_HEIGHT),
      // Älterer Text kennt die Felder nicht: Er ist mitgewachsen, also bleibt er es.
      autoHeight: map.get('autoHeight') !== false,
      valign: (map.get('valign') as 'top' | 'middle' | 'bottom') ?? 'top',
    }
  }

  if (type === 'iframe') {
    return {
      ...base,
      type,
      url: String(map.get('url') ?? ''),
      // Ältere Elemente kennen die Felder noch nicht.
      viewportWidth: Number(map.get('viewportWidth') ?? DEFAULT_IFRAME_VIEWPORT),
      pageFactor: Number(map.get('pageFactor') ?? DEFAULT_PAGE_FACTOR),
      scrollY: Number(map.get('scrollY') ?? 0),
    }
  }
  return {
    ...base,
    type,
    mediaId: String(map.get('mediaId') ?? ''),
    naturalW: Number(map.get('naturalW') ?? base.w),
    naturalH: Number(map.get('naturalH') ?? base.h),
  }
}

/** Die Elemente eines beliebigen Dokuments — auch eines aus dem Verlauf. */
export const elementsOf = (doc: Y.Doc): BoardElement[] => readAll(elementsMap(doc))

function readAll(map: Y.Map<YElement>): BoardElement[] {
  const result: BoardElement[] = []
  map.forEach((value, key) => {
    const element = readElement(key, value)
    if (element) result.push(element)
  })
  // Stapelreihenfolge steckt in der DOM-Reihenfolge; z-index pro Element wäre
  // bei hunderten Elementen unnötig viel Arbeit für den Compositor.
  result.sort((a, b) => a.z - b.z)
  return result
}

/**
 * Alle Elemente des Boards als React-Snapshot.
 *
 * Der Cache ist nötig, weil `getSnapshot` bei jedem Render aufgerufen wird und
 * ein jedes Mal neu gebautes Array React in eine Endlosschleife schicken würde.
 */
export function useBoardElements(doc: Y.Doc): BoardElement[] {
  const map = elementsMap(doc)
  const cache = useRef<BoardElement[]>([])
  const stale = useRef(true)

  const subscribe = useCallback(
    (onChange: () => void) => {
      const handler = (): void => {
        stale.current = true
        onChange()
      }
      map.observeDeep(handler)
      return () => map.unobserveDeep(handler)
    },
    [map],
  )

  const getSnapshot = useCallback(() => {
    if (stale.current) {
      cache.current = readAll(map)
      stale.current = false
    }
    return cache.current
  }, [map])

  return useSyncExternalStore(subscribe, getSnapshot)
}

export function nextZ(doc: Y.Doc): number {
  let max = 0
  elementsMap(doc).forEach((element) => {
    max = Math.max(max, Number(element.get('z') ?? 0))
  })
  return max + 1
}

export function addElement(doc: Y.Doc, element: BoardElement): void {
  const map = new Y.Map<unknown>()
  doc.transact(() => {
    for (const [key, value] of Object.entries(element)) {
      if (key === 'id') continue
      map.set(key, value)
    }
    elementsMap(doc).set(element.id, map)
  })
}

/**
 * Teilweises Update.
 *
 * Es werden gezielt einzelne Felder gesetzt, nie das ganze Element ersetzt —
 * so überleben gleichzeitige Änderungen an *anderen* Feldern durch andere
 * Nutzer. Genau dafür ist jedes Element ein eigenes `Y.Map`.
 */
export function updateElement(doc: Y.Doc, id: string, patch: Partial<BoardElement>): void {
  const element = elementsMap(doc).get(id)
  if (!element) return
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'id' || value === undefined) continue
      element.set(key, value)
    }
  })
}

/**
 * Das Yjs-Fragment eines Textelements — die Fassung, über die gleichzeitiges
 * Tippen zusammenläuft.
 *
 * Es entsteht erst, wenn zum ersten Mal jemand den Text bearbeitet. Gesät wird
 * es aus dem, was da ist: aus der ausgezeichneten Struktur, sonst aus dem
 * reinen Text. Damit kommt auch Text, der lange vor dem Editor entstanden ist,
 * unverändert im Editor an — und Text aus einer Vorlage ebenso.
 */
export async function richFragment(doc: Y.Doc, id: string): Promise<Y.XmlFragment | null> {
  const element = elementsMap(doc).get(id)
  if (!element) return null

  const vorhanden = element.get('richY')
  if (vorhanden instanceof Y.XmlFragment) return vorhanden

  /*
   * Editor und Schema kommen erst hier dazu.
   *
   * Sie machen ein Drittel des Bündels aus, werden aber nur gebraucht, wenn
   * jemand tatsächlich tippt. Wer ein Board bloß ansieht — jeder Gast, jeder
   * Zuschauer einer Vorführung — lädt sie nie.
   */
  const [{ SCHEMA }, { prosemirrorJSONToYXmlFragment }] = await Promise.all([
    import('../canvas/RichEditor.js'),
    import('y-prosemirror'),
  ])

  // Nach dem Warten noch einmal nachsehen: Inzwischen kann ein anderer
  // Teilnehmer das Fragment angelegt haben.
  const inzwischen = element.get('richY')
  if (inzwischen instanceof Y.XmlFragment) return inzwischen

  const rich = element.get('rich')
  const start: RichDoc = isRichDoc(rich) ? rich : richFromPlain(String(element.get('text') ?? ''))
  const fragment = new Y.XmlFragment()
  doc.transact(() => {
    element.set('richY', fragment)
    // Erst einhängen, dann füllen: Ein Fragment ohne Dokument dahinter nimmt
    // keine Inhalte an.
    prosemirrorJSONToYXmlFragment(SCHEMA, start, fragment)
    if (!isRichDoc(rich)) element.set('rich', start)
  })
  return fragment
}

export function removeElement(doc: Y.Doc, id: string): void {
  doc.transact(() => elementsMap(doc).delete(id))
}

export function bringToFront(doc: Y.Doc, id: string): void {
  updateElement(doc, id, { z: nextZ(doc) })
}

/**
 * Verschiebt ein Element um eine Position in der Stapelreihenfolge.
 *
 * Anschließend wird durchnummeriert — bei einer überschaubaren Zahl von
 * Elementen unproblematisch und deutlich einfacher als Bruchzahl-Ordnungen.
 */
export function moveElementLayer(
  doc: Y.Doc,
  ordered: readonly { id: string }[],
  id: string,
  delta: number,
): void {
  const index = ordered.findIndex((element) => element.id === id)
  const target = index + delta
  if (index < 0 || target < 0 || target >= ordered.length) return

  const next = [...ordered]
  const [moved] = next.splice(index, 1)
  if (!moved) return
  next.splice(target, 0, moved)

  doc.transact(() => {
    const map = elementsMap(doc)
    next.forEach((element, position) => map.get(element.id)?.set('z', position + 1))
  })
}

/**
 * Sortiert `id` direkt vor `beforeId` in der Stapelreihenfolge ein.
 *
 * „Vor" heißt hier weiter hinten im Stapel — die Ebenenliste zeigt das
 * vorderste Element oben, also entspricht ein Ablegen *auf* einem Eintrag dem
 * Einsortieren an dessen Stelle.
 */
export function reorderElement(
  doc: Y.Doc,
  ordered: readonly { id: string }[],
  id: string,
  beforeId: string,
): void {
  const from = ordered.findIndex((element) => element.id === id)
  const to = ordered.findIndex((element) => element.id === beforeId)
  if (from < 0 || to < 0 || from === to) return

  const next = [...ordered]
  const [moved] = next.splice(from, 1)
  if (!moved) return
  next.splice(to, 0, moved)

  doc.transact(() => {
    const map = elementsMap(doc)
    next.forEach((element, position) => map.get(element.id)?.set('z', position + 1))
  })
}

export function createId(): string {
  return crypto.randomUUID()
}
